// =============================================================================
// complianceShared — the vocabulary every compliance screen speaks
// =============================================================================
// One place for the five states, their colours, their words and the bar that
// draws them. The matrix, the team view and a worker's own portal page all read
// from here, so a status can never mean one thing on one screen and something
// else on another.
//
// DESIGN RULES THIS FOLLOWS
// -------------------------
// * Status colour is NEVER the only signal. Every state ships with an icon and a
//   word as well, so it survives colourblindness, a photocopier, and a phone in
//   direct sun on a tower site - which is where this actually gets read.
// * The bar carries the colour; the text does not. Values and labels wear text
//   tokens. A tinted status pill is the one exception, and it always has its
//   label inside it.
// * 2px of surface between touching segments, never a stroke. The gap does the
//   separating; a border would add ink that is not data.
// * Colours come from Coretel's own tokens (ok / warn / danger / info), which
//   already resolve correctly in light and dark mode. Nothing is hard-coded.
// =============================================================================

import type { LucideIcon } from "lucide-react";
import {
  CheckCircle2, Clock, ShieldQuestion, AlertTriangle, MinusCircle,
} from "lucide-react";

// -----------------------------------------------------------------------------
// The five states a requirement can be in
// -----------------------------------------------------------------------------
export type ReqStatus = "current" | "expiring" | "pending" | "expired" | "missing";

export interface StatusMeta {
  label:  string;       // what it is called on screen
  blurb:  string;       // what it means, in plain words
  icon:   LucideIcon;
  fill:   string;       // the bar segment
  text:   string;       // the pill's text colour
  chip:   string;       // the pill's background
  counts: boolean;      // does it count towards being site ready?
}

// Order matters: this is best -> worst, and the bar draws in this order.
export const STATUS_ORDER: ReqStatus[] = ["current", "expiring", "pending", "expired", "missing"];

export const STATUS_META: Record<ReqStatus, StatusMeta> = {
  current: {
    label: "Current", blurb: "Held and in date.",
    icon: CheckCircle2,
    fill: "bg-ok", text: "text-ok", chip: "bg-ok/12", counts: true,
  },
  expiring: {
    // Deliberately counts as compliant. A ticket valid for another three weeks
    // IS valid today - calling it non-compliant makes the number cry wolf and
    // people stop trusting the colour. It is surfaced separately so it still
    // gets chased.
    label: "Expiring", blurb: "Still valid, but inside 60 days.",
    icon: Clock,
    fill: "bg-warn", text: "text-warn", chip: "bg-warn/12", counts: true,
  },
  pending: {
    label: "Not verified", blurb: "Uploaded, but nobody has checked it yet.",
    icon: ShieldQuestion,
    fill: "bg-info", text: "text-info", chip: "bg-info/12", counts: false,
  },
  expired: {
    label: "Expired", blurb: "Held, but out of date.",
    icon: AlertTriangle,
    fill: "bg-danger", text: "text-danger", chip: "bg-danger/12", counts: false,
  },
  missing: {
    // Neutral, not red: absence is a different thing from a lapse, and drawing
    // both in danger red makes a person with nothing loaded look identical to a
    // person whose ticket ran out yesterday.
    label: "Missing", blurb: "Nothing on file for this.",
    icon: MinusCircle,
    fill: "bg-text-dim/35", text: "text-text-muted", chip: "bg-text/8", counts: false,
  },
};

export interface StatusCounts {
  current?: number; expiring?: number; pending?: number; expired?: number; missing?: number;
}

// -----------------------------------------------------------------------------
// The verdict — one sentence a person can act on
// -----------------------------------------------------------------------------
export type ReadinessBand = "ready" | "renewals" | "gaps" | "not_ready" | "unset";

export interface Readiness {
  band:  ReadinessBand;
  label: string;
  icon:  LucideIcon;
  text:  string;
  chip:  string;
}

export function readiness(pct: number | null, c: StatusCounts, required: number): Readiness {
  if (!required) {
    return { band: "unset", label: "No requirements set", icon: MinusCircle,
             text: "text-text-muted", chip: "bg-text/8" };
  }
  const p = pct ?? 0;
  if (p >= 100) {
    return (c.expiring ?? 0) > 0
      ? { band: "renewals", label: "Ready — renewals due", icon: Clock,
          text: "text-warn", chip: "bg-warn/12" }
      : { band: "ready", label: "Site ready", icon: CheckCircle2,
          text: "text-ok", chip: "bg-ok/12" };
  }
  if (p >= 50) {
    return { band: "gaps", label: "Gaps to close", icon: Clock,
             text: "text-warn", chip: "bg-warn/12" };
  }
  return { band: "not_ready", label: "Not site ready", icon: AlertTriangle,
           text: "text-danger", chip: "bg-danger/12" };
}

// -----------------------------------------------------------------------------
// ReadinessBar — a 100% stacked bar, one segment per state
// -----------------------------------------------------------------------------
// Thin on purpose. It is a glance, not a chart to be studied: the number beside
// it and the list underneath carry the detail.
//
// No labels inside the segments - at this height nothing would fit, and a
// cropped label is worse than none. The legend and the list carry identity.
// -----------------------------------------------------------------------------
export function ReadinessBar({
  counts, height = 8, title,
}: { counts: StatusCounts; height?: number; title?: string }) {
  const total = STATUS_ORDER.reduce((n, s) => n + (counts[s] ?? 0), 0);

  if (!total) {
    return (
      <div className="w-full rounded-full bg-text/8" style={{ height }} title="Nothing required yet" />
    );
  }

  const parts = STATUS_ORDER
    .map(s => ({ s, n: counts[s] ?? 0 }))
    .filter(p => p.n > 0);

  return (
    <div
      className="w-full flex rounded-full overflow-hidden"
      style={{ height, gap: 2 }}
      title={title ?? parts.map(p => `${p.n} ${STATUS_META[p.s].label.toLowerCase()}`).join(" · ")}
      role="img"
      aria-label={parts.map(p => `${p.n} ${STATUS_META[p.s].label.toLowerCase()}`).join(", ")}
    >
      {parts.map(({ s, n }, i) => (
        <div
          key={s}
          className={STATUS_META[s].fill}
          style={{
            flexGrow: n,
            flexBasis: 0,
            // Rounded at the two outer ends only; the 2px gaps do the rest.
            borderTopLeftRadius:     i === 0 ? 4 : 0,
            borderBottomLeftRadius:  i === 0 ? 4 : 0,
            borderTopRightRadius:    i === parts.length - 1 ? 4 : 0,
            borderBottomRightRadius: i === parts.length - 1 ? 4 : 0,
          }}
        />
      ))}
    </div>
  );
}

// -----------------------------------------------------------------------------
// Legend — shown once per list, never per row
// -----------------------------------------------------------------------------
export function ReadinessLegend({ counts }: { counts?: StatusCounts }) {
  return (
    <div className="flex items-center gap-x-4 gap-y-1.5 flex-wrap">
      {STATUS_ORDER.map(s => {
        const m = STATUS_META[s];
        const n = counts?.[s];
        return (
          <span key={s} className="inline-flex items-center gap-1.5 text-[11px] text-text-muted"
                title={m.blurb}>
            <span className={`w-2.5 h-2.5 rounded-sm ${m.fill}`} />
            {m.label}
            {n != null && <span className="font-mono text-text">{n}</span>}
          </span>
        );
      })}
    </div>
  );
}

// -----------------------------------------------------------------------------
// StatusPill — colour, icon and word together, always
// -----------------------------------------------------------------------------
export function StatusPill({ status, small = false }: { status: ReqStatus; small?: boolean }) {
  const m = STATUS_META[status];
  const Icon = m.icon;
  return (
    <span
      className={`inline-flex items-center gap-1 rounded ${m.chip} ${m.text} ${
        small ? "px-1.5 py-0.5 text-[10px]" : "px-2 py-0.5 text-[11px]"
      } font-medium whitespace-nowrap`}
      title={m.blurb}
    >
      <Icon size={small ? 10 : 12} /> {m.label}
    </span>
  );
}

// -----------------------------------------------------------------------------
// Role kinds — the four groupings from Ventia's RRF
// -----------------------------------------------------------------------------
export const ROLE_KIND_META: Record<string, { label: string; blurb: string; order: number }> = {
  primary: { label: "Primary role",  blurb: "What they are",                    order: 1 },
  task:    { label: "Task roles",    blurb: "What they're also signed off for", order: 2 },
  plant:   { label: "Plant & vehicles", blurb: "What they operate",             order: 3 },
  access:  { label: "Site access",   blurb: "Whose sites they can enter",       order: 4 },
};

export function roleKindLabel(kind: string): string {
  return ROLE_KIND_META[kind]?.label ?? kind;
}

// The status a group of alternatives resolves to — best wins, because any one
// of them satisfies the requirement.
export function bestStatus(list: ReqStatus[]): ReqStatus {
  for (const s of STATUS_ORDER) if (list.includes(s)) return s;
  return "missing";
}

export function statusFromRank(rank: number): ReqStatus {
  return (["current", "expiring", "pending", "expired", "missing"][rank - 1] as ReqStatus) ?? "missing";
}

// -----------------------------------------------------------------------------
// ReadinessRing — the hero number, as one arc
// -----------------------------------------------------------------------------
// A single value, so a ring is honest here in a way a donut of categories never
// is: there is nothing to compare between slices, just one quantity against its
// whole. The stacked bar beside it carries the breakdown.
//
// The number is inside the ring, not implied by it - the arc is reinforcement,
// and it still reads at a glance on a phone held at arm's length in the sun.
// -----------------------------------------------------------------------------
export function ReadinessRing({
  pct, colorClass, size = 112, stroke = 10,
}: { pct: number | null; colorClass: string; size?: number; stroke?: number }) {
  const r = (size - stroke) / 2;
  const circumference = 2 * Math.PI * r;
  const p = Math.max(0, Math.min(100, pct ?? 0));
  const dash = (p / 100) * circumference;

  return (
    <div className="relative mx-auto" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90" aria-hidden>
        <circle cx={size / 2} cy={size / 2} r={r} fill="none"
                strokeWidth={stroke} stroke="currentColor" className="text-text/[0.08]" />
        <circle cx={size / 2} cy={size / 2} r={r} fill="none"
                strokeWidth={stroke} stroke="currentColor" strokeLinecap="round"
                className={colorClass}
                strokeDasharray={`${dash} ${circumference - dash}`}
                style={{ transition: "stroke-dasharray 700ms cubic-bezier(.2,.8,.2,1)" }} />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-[28px] font-semibold tabular-nums leading-none text-text">
          {pct == null ? "—" : `${pct}%`}
        </span>
        <span className="text-[9px] uppercase tracking-wider2 text-text-muted font-mono mt-1">
          ready
        </span>
      </div>
    </div>
  );
}


// -----------------------------------------------------------------------------
// Requirement rows → one readiness summary. Extracted from PortalHome so the
// employee file can show the SAME figure for a person that the portal shows
// them about themselves — two derivations of "site ready" would eventually
// disagree, and this number gets quoted to clients.
// -----------------------------------------------------------------------------
export interface RequirementRow {
  role_id: string; role_name: string; alt_group: string; is_mandatory: boolean;
  licence_type_id: string; licence_name: string; licence_id: string | null;
  expiry_date: string | null; status: ReqStatus;
}

export function summariseRequirements(reqs: RequirementRow[]) {
  // Stage 1: within one role, one alt_group = one need with OR'd options.
  const pairs = new Map<string, { opts: Map<string, RequirementRow>; mand: boolean }>();
  for (const r of reqs) {
    const k = `${r.role_id}::${r.alt_group}`;
    const e = pairs.get(k) ?? { opts: new Map(), mand: r.is_mandatory };
    e.opts.set(r.licence_type_id, r);
    e.mand = e.mand || r.is_mandatory;
    pairs.set(k, e);
  }

  // Stage 2: the same ticket set wanted by two roles is one thing to hold.
  const merged = new Map<string, {
    names: string[]; status: ReqStatus; expiry: string | null; mand: boolean;
  }>();
  for (const e of pairs.values()) {
    const opts = Array.from(e.opts.values())
      .sort((a, b) => a.licence_name.localeCompare(b.licence_name));
    const key = opts.map(o => o.licence_type_id).join("|");
    const status = bestStatus(opts.map(o => o.status));
    const g = merged.get(key) ?? {
      names: opts.map(o => o.licence_name),
      status,
      expiry: opts.find(o => o.status === status)?.expiry_date ?? null,
      mand: false,
    };
    g.mand = g.mand || e.mand;
    merged.set(key, g);
  }

  const list = Array.from(merged.values());
  const counts: StatusCounts = {};
  for (const g of list) if (g.mand) counts[g.status] = (counts[g.status] ?? 0) + 1;

  const required = list.filter(g => g.mand).length;
  const met = (counts.current ?? 0) + (counts.expiring ?? 0);
  const pct = required ? Math.round((100 * met) / required) : null;

  return { list, counts, required, met, pct, verdict: readiness(pct, counts, required) };
}
